/* План и факт проекта (ТЗ 5.13) — отдельная страница, как все формы.
   /project/:id/plan. MRR — подписка по сделке проекта; план — чек и себестоимость;
   факт — деньги, внесённые руками: у старых проектов журнала не было, и полученное
   записывается цифрой, а операции из журнала прибавляются к ней сверху. */

import { canEdit, crmLevel } from "@mera/shared";
import { Button, List } from "konsta/react";
import React, { useState } from "react";
import { useNavigate, useParams } from "../lib/router.jsx";

import Screen from "../components/Screen.jsx";
import { SkelForm } from "../components/skeleton.jsx";
import { ErrorState, MoneyInput } from "../components/ui.jsx";
import { api } from "../lib/api.js";
import { useApp, useFetch } from "../lib/store.jsx";
import { haptic } from "../lib/tg.js";

export default function ProjectPlan() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { me } = useApp();
  const { data: p, loading, error, refetch } = useFetch(`/projects/${id}`);

  const [f, setF] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  // Форма заполняется один раз, когда проект приехал
  React.useEffect(() => {
    if (f || !p) return;
    setF({
      income: p.income ?? "",
      expenses: p.expenses ?? "",
      factIncome: p.factIncome || "",
      factExpenses: p.factExpenses || "",
      mrr: p.deal?.mrr ?? "",
    });
  }, [f, p]);

  if (loading || (!f && !error)) return <Screen back title="План и факт"><SkelForm /></Screen>;
  if (error) return <Screen back><ErrorState text={error} onRetry={refetch} /></Screen>;
  if (!canEdit(crmLevel(me, p.businessId, "accounting"))) {
    return <Screen back><ErrorState text="У вас доступ только на чтение — правки здесь закрыты." /></Screen>;
  }

  // У Dialogic-аккаунтинга те же поля зовутся языком подписки
  const isDialogicAcc = p.metrics?.kind === "dialogic-accounting";
  const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.patch(`/projects/${id}`, {
        income: Number(f.income) || 0,
        expenses: Number(f.expenses) || 0,
        factIncome: Number(f.factIncome) || 0,
        factExpenses: Number(f.factExpenses) || 0,
        // MRR живёт на сделке проекта; без сделки его некуда записать
        ...(p.dealId && { mrr: f.mrr === "" ? null : Number(f.mrr) || 0 }),
      });
      haptic.success();
      navigate(-1);
    } catch (e) {
      haptic.error();
      setErr(e.message);
      setBusy(false);
    }
  };

  return (
    <Screen back title="План и факт">
      {p.dealId ? (
        <List strong inset>
          <MoneyInput placeholder="MRR, ₽ в месяц" value={f.mrr} onChange={set("mrr")} />
        </List>
      ) : null}

      <List strong inset>
        <MoneyInput
          placeholder={isDialogicAcc ? "Стоимость внедрения, ₽" : "План доходов (чек), ₽"}
          value={f.income}
          onChange={set("income")}
        />
        <MoneyInput
          placeholder={isDialogicAcc ? "Себестоимость, ₽" : "План расходов, ₽"}
          value={f.expenses}
          onChange={set("expenses")}
        />
      </List>

      <List strong inset>
        <MoneyInput placeholder="Факт · получено, ₽" value={f.factIncome} onChange={set("factIncome")} />
        <MoneyInput placeholder="Факт · потрачено, ₽" value={f.factExpenses} onChange={set("factExpenses")} />
      </List>
      <div className="px-8 -mt-6 text-[13px]" style={{ color: "var(--tg-hint)" }}>
        Факт — для проектов, по которым журнал не вёлся: старые деньги записываются
        сюда цифрой, а операции из журнала прибавляются к ней сверху.
      </div>

      {err ? (
        <div className="px-4 pt-1 text-center text-[13px]" style={{ color: "var(--fin-red)" }}>{err}</div>
      ) : null}

      <div className="px-4 pt-3">
        <Button large rounded onClick={save} disabled={busy}>
          Сохранить
        </Button>
      </div>
    </Screen>
  );
}
